import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Instagram, Facebook } from "lucide-react";
import { DialogClose } from "@/components/ui/dialog";
import { calculateAge } from "@/utils/ageValidation";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileDateWheel } from "@/components/ui/mobile-date-wheel";

// Full schema for complete profile validation (when fieldToEdit is undefined)
const profileFormSchema = z.object({
  name: z.string().min(2, {
    message: "Name must contain at least 2 characters",
  }),
  nickname: z.string()
    .min(5, {
      message: "Nickname must contain at least 5 characters and can only contain letters, numbers, dots, underscores",
    })
    .max(30, {
      message: "Nickname cannot exceed 30 characters",
    })
    .regex(/^[a-zA-Z0-9._]+$/, {
      message: "Nickname can only contain letters, numbers, dots, underscores",
    }),
  email: z.string().email({
    message: "Please enter a valid email",
  }),
  birthday: z.string()
    .optional()
    .refine((date) => {
      if (!date || date === '') return true; // Explicitly handle empty string
      return /^\d{4}-\d{2}-\d{2}$/.test(date);
    }, "Please enter date in YYYY-MM-DD format")
    .refine((date) => {
      if (!date || date === '') return true;
      const age = calculateAge(date);
      return age >= 16;
    }, "You must be at least 16 years old")
    .refine((date) => {
      if (!date || date === '') return true;
      const age = calculateAge(date);
      return age <= 120;
    }, "Please enter a valid birth date"),
  phoneNumber: z.string().optional(),
  gender: z.string().optional(),
  otherGender: z.string().optional(),
  bio: z.string()
    .max(500, {
      message: "Bio must not exceed 500 characters",
    })
    .regex(/^[a-zA-Z0-9.\s]*$/, {
      message: "Bio can only contain letters, numbers, dots, and spaces",
    })
    .optional(),
  instagram: z.string().optional(),
  facebook: z.string().optional(),
});

// Field-specific schemas - only validate the field being edited
const nameOnlySchema = z.object({
  name: z.string().min(2, { message: "Name must contain at least 2 characters" }),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

const nicknameOnlySchema = z.object({
  nickname: z.string()
    .min(5, { message: "Nickname must contain at least 5 characters and can only contain letters, numbers, dots, underscores" })
    .max(30, { message: "Nickname cannot exceed 30 characters" })
    .regex(/^[a-zA-Z0-9._]+$/, { message: "Nickname can only contain letters, numbers, dots, underscores" }),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

const phoneNumberOnlySchema = z.object({
  phoneNumber: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

const bioOnlySchema = z.object({
  bio: z.string()
    .max(500, { message: "Bio must not exceed 500 characters" })
    .regex(/^[a-zA-Z0-9.\s]*$/, { message: "Bio can only contain letters, numbers, dots, and spaces" })
    .nullable().optional(),
  name: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

const instagramOnlySchema = z.object({
  instagram: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

const facebookOnlySchema = z.object({
  facebook: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
});

const birthdayOnlySchema = z.object({
  birthday: z.string()
    .nullable()
    .optional()
    .refine((date) => {
      if (!date || date === '') return true;
      return /^\d{4}-\d{2}-\d{2}$/.test(date);
    }, "Please enter date in YYYY-MM-DD format")
    .refine((date) => {
      if (!date || date === '') return true;
      const age = calculateAge(date);
      return age >= 16;
    }, "You must be at least 16 years old")
    .refine((date) => {
      if (!date || date === '') return true;
      const age = calculateAge(date);
      return age <= 120;
    }, "Please enter a valid birth date"),
  name: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

const genderOnlySchema = z.object({
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  facebook: z.string().nullable().optional(),
});

// Dynamic schema selector based on fieldToEdit
const getSchemaForField = (fieldToEdit?: string) => {
  if (!fieldToEdit) return profileFormSchema; // Full form validation

  switch(fieldToEdit) {
    case 'name': return nameOnlySchema;
    case 'nickname': return nicknameOnlySchema;
    case 'phoneNumber': return phoneNumberOnlySchema;
    case 'bio': return bioOnlySchema;
    case 'instagram': return instagramOnlySchema;
    case 'facebook': return facebookOnlySchema;
    case 'birthday': return birthdayOnlySchema;
    case 'gender': return genderOnlySchema;
    default: return profileFormSchema; // Fallback to full validation
  }
};

interface ProfileFormProps {
  defaultValues: {
    name: string;
    email: string;
    birthday?: string;
    phoneNumber: string;
    nickname: string;
    gender?: string;
    otherGender?: string;
    bio?: string;
    instagram?: string;
    facebook?: string;
  };
  fieldToEdit?: string;
  onClose?: () => void;
  isOpen?: boolean;
}

// Helper function to normalize empty values to null
const normalizeValue = (value: string | null | undefined): string | null => {
  if (value === "" || value === undefined) return null;
  return value;
};

export const ProfileForm = ({ defaultValues, fieldToEdit, onClose, isOpen = true }: ProfileFormProps) => {
  const { toast } = useToast();
  const { user, updateUserField } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();

  const form = useForm({
    resolver: zodResolver(getSchemaForField(fieldToEdit)),
    defaultValues,
  });

  useEffect(() => {
    if (user) {
      const currentValues = form.getValues();
      const isOtherGender = user.gender && !['male', 'female', 'non-binary', 'prefer_not_to_say'].includes(user.gender);

      if (
        user.full_name !== currentValues.name ||
        user.email !== currentValues.email ||
        user.birthday !== currentValues.birthday ||
        user.phone_number !== currentValues.phoneNumber ||
        user.nickname !== currentValues.nickname ||
        user.gender !== currentValues.gender ||
        user.bio !== currentValues.bio ||
        user.instagram !== currentValues.instagram ||
        user.facebook !== currentValues.facebook
      ) {
        form.reset({
          name: user.full_name,
          email: user.email,
          birthday: user.birthday,
          phoneNumber: user.phone_number || '',
          nickname: user.nickname,
          gender: isOtherGender ? 'other' : (user.gender || ''),
          otherGender: isOtherGender ? user.gender : '',
          bio: user.bio || '',
          instagram: user.instagram || '',
          facebook: user.facebook || '',
        });
      }
    }
  }, [user, form]);

  const onSubmit = async (data: z.infer<typeof profileFormSchema>) => {

    setIsLoading(true);
    if (!user) {
      toast({
        title: "Error",
        description: "User not found",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const updates = [];

      const formValues = form.getValues();
      const currentValues = {
        full_name: user.full_name,
        phone_number: user.phone_number || '',
        nickname: user.nickname,
        birthday: user.birthday,
        gender: user.gender || '',
        bio: user.bio || '',
        instagram: user.instagram || '',
        facebook: user.facebook || '',
      };

      if (formValues.name !== currentValues.full_name) {
        updates.push(updateUserField(user.id, 'full_name', formValues.name));
      }
      if (normalizeValue(formValues.phoneNumber) !== normalizeValue(currentValues.phone_number)) {
        updates.push(updateUserField(user.id, 'phone_number', normalizeValue(formValues.phoneNumber)));
      }
      if (formValues.nickname !== currentValues.nickname) {
        updates.push(updateUserField(user.id, 'nickname', formValues.nickname));
      }
      if (formValues.birthday !== currentValues.birthday) {
        updates.push(updateUserField(user.id, 'birthday', formValues.birthday));
      }
      if (formValues.gender !== currentValues.gender) {
        const genderValue = formValues.gender === 'other' ? formValues.otherGender : formValues.gender;
        updates.push(updateUserField(user.id, 'gender', genderValue));
      }
      if (normalizeValue(formValues.bio) !== normalizeValue(currentValues.bio)) {
        updates.push(updateUserField(user.id, 'bio', normalizeValue(formValues.bio)));
      }
      if (normalizeValue(formValues.instagram) !== normalizeValue(currentValues.instagram)) {
        updates.push(updateUserField(user.id, 'instagram', normalizeValue(formValues.instagram)));
      }
      if (normalizeValue(formValues.facebook) !== normalizeValue(currentValues.facebook)) {
        updates.push(updateUserField(user.id, 'facebook', normalizeValue(formValues.facebook)));
      }


      if (updates.length > 0) {
        const results = await Promise.all(updates);
        const updatedUser = results[results.length - 1];


        if (updatedUser) {
          toast({
            title: "Success",
            description: "Your changes have been successfully saved",
          });
        }
      } else {
        toast({
          title: "No Changes",
          description: "No changes were made to your profile",
        });
      }

      // Only close the dialog after successful submission
      if (onClose) {
        onClose();
      }

    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = () => {
    switch (fieldToEdit) {
      case 'name':
        return (
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'nickname':
        return (
          <FormField
            control={form.control}
            name="nickname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nickname</FormLabel>
                <FormControl>
                  <Input {...field} maxLength={30} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'phoneNumber':
        return (
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+1 (234) 567-8900"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'bio':
        return (
          <FormField
            control={form.control}
            name="bio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bio</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Tell us about yourself..."
                    className="resize-none"
                    rows={4}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'gender':
        return (
          <>
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Gender</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value || ''}
                      className="space-y-1"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="male" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Male
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="female" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Female
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="non-binary" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Non-binary
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="prefer_not_to_say" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Prefer not to say
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="other" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Other
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch('gender') === 'other' && (
              <FormField
                control={form.control}
                name="otherGender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Specify gender</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter your gender" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </>
        );
      case 'instagram':
        return (
          <FormField
            control={form.control}
            name="instagram"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Instagram className="h-4 w-4 text-[#E1306C]" />
                  Instagram
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Your Instagram handle"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'facebook':
        return (
          <FormField
            control={form.control}
            name="facebook"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Facebook className="h-4 w-4 text-[#1877F2]" />
                  Facebook
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Your Facebook profile URL"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'birthday':
        return (
          <FormField
            control={form.control}
            name="birthday"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Birthday</FormLabel>
                <FormControl>
                  {isMobile ? (
                    <MobileDateWheel
                      value={field.value || ''}
                      onChange={field.onChange}
                      minAge={16}
                      maxAge={120}
                    />
                  ) : (
                    <Input
                      type="date"
                      {...field}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      default:
        return null;
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    form.handleSubmit(onSubmit)(e);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleFormSubmit} className="space-y-6">
        {renderField()}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={isLoading}
            onClick={() => console.log('Save button clicked, isLoading:', isLoading)}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
};
